import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurant } from "@/hooks/useRestaurant";
import { normalizeTableNumber } from "@/lib/utils";

interface TableConsumption {
  tableNumber: string;
  total: number;
  orderId: string;
  lastItemAt: Date | null;
}

export function useTableConsumptionValues(inactivityMinutes: number = 15) {
  const { activeRestaurant } = useRestaurant();
  const [consumptionMap, setConsumptionMap] = useState<Map<string, TableConsumption>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [, setTick] = useState(0);

  // Timer to re-render every minute for inactivity check
  useEffect(() => {
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchConsumptionValues = useCallback(async () => {
    if (!activeRestaurant) {
      setConsumptionMap(new Map());
      return;
    }

    setIsLoading(true);
    try {
      // Fetch all orders that are pending/preparing for tables
      const { data: orders, error } = await supabase
        .from("orders")
        .select("id, table_number, total, created_at")
        .eq("restaurant_id", activeRestaurant.id)
        .in("status", ["pending", "preparing"])
        .eq("order_type", "table")
        .not("table_number", "is", null);

      if (error) throw error;

      if (!orders || orders.length === 0) {
        setConsumptionMap(new Map());
        setIsLoading(false);
        return;
      }

      // Fetch last item timestamp for each order
      const orderIds = orders.map(o => o.id);
      const { data: lastItems, error: itemsError } = await supabase
        .from("order_items")
        .select("order_id, created_at")
        .in("order_id", orderIds)
        .order("created_at", { ascending: false });

      if (itemsError) throw itemsError;

      // Build a map of order_id -> last item timestamp
      const lastItemMap = new Map<string, Date>();
      if (lastItems) {
        lastItems.forEach(item => {
          if (!lastItemMap.has(item.order_id)) {
            lastItemMap.set(item.order_id, new Date(item.created_at));
          }
        });
      }

      // Build order map by id for grouped lookups
      const orderById = new Map<string, typeof orders[0]>();
      orders.forEach(o => orderById.set(o.id, o));

      const newMap = new Map<string, TableConsumption>();
      
      orders.forEach((order) => {
        if (order.table_number) {
          const normalizedNumber = normalizeTableNumber(order.table_number);
          const lastItemAt = lastItemMap.get(order.id) || new Date(order.created_at);
          newMap.set(normalizedNumber, {
            tableNumber: normalizedNumber,
            total: Number(order.total) || 0,
            orderId: order.id,
            lastItemAt,
          });
        }
      });

      // Fetch grouped tables and point them to the main order's consumption
      const { data: groupedTables } = await supabase
        .from("tables")
        .select("number, grouped_to_order_id")
        .eq("restaurant_id", activeRestaurant.id)
        .not("grouped_to_order_id", "is", null);

      if (groupedTables) {
        for (const gt of groupedTables) {
          const mainOrder = orderById.get(gt.grouped_to_order_id!);
          if (mainOrder) {
            const normalizedNumber = normalizeTableNumber(gt.number);
            const lastItemAt = lastItemMap.get(mainOrder.id) || new Date(mainOrder.created_at);
            newMap.set(normalizedNumber, {
              tableNumber: normalizedNumber,
              total: Number(mainOrder.total) || 0,
              orderId: mainOrder.id,
              lastItemAt,
            });
          }
        }
      }

      setConsumptionMap(newMap);
    } catch (error) {
      console.error("Error fetching table consumption values:", error);
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant]);

  useEffect(() => {
    fetchConsumptionValues();
  }, [fetchConsumptionValues]);

  // Real-time subscription for order and order_items updates
  useEffect(() => {
    if (!activeRestaurant) return;

    const channel = supabase
      .channel("table-consumption-updates")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "orders",
          filter: `restaurant_id=eq.${activeRestaurant.id}`,
        },
        () => {
          fetchConsumptionValues();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "order_items",
        },
        () => {
          fetchConsumptionValues();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeRestaurant, fetchConsumptionValues]);

  const getTableConsumption = useCallback((tableNumber: string) => {
    const normalized = normalizeTableNumber(tableNumber);
    return consumptionMap.get(normalized);
  }, [consumptionMap]);

  const isTableInactive = useCallback((tableNumber: string) => {
    const normalized = normalizeTableNumber(tableNumber);
    const consumption = consumptionMap.get(normalized);
    if (!consumption || !consumption.lastItemAt) return false;
    
    const now = new Date();
    const diffMinutes = (now.getTime() - consumption.lastItemAt.getTime()) / (1000 * 60);
    return diffMinutes >= inactivityMinutes;
  }, [consumptionMap, inactivityMinutes]);

  return {
    consumptionMap,
    isLoading,
    getTableConsumption,
    isTableInactive,
    refetch: fetchConsumptionValues,
  };
}
